import type { OperationItem } from "~/pages/SupplierPortal/TraceabilityRequests/utils"

export type TraceabilityMatrixCell =
  | { op: OperationItem; rowspan: number }
  | "no-op"

export function getSortedRanks(
  operationItems: readonly OperationItem[]
): string[] {
  return [...new Set(operationItems.map((item) => item.rank))]
    .sort((a, b) => a - b)
    .map((rank) => String(rank))
}

export function buildRows(
  operationItems: readonly OperationItem[],
  ranks: readonly string[]
): TraceabilityMatrixCell[][] {
  if (operationItems.length === 0) {
    return []
  }

  const sortedRanks = ranks.map((rank) => Number(rank))
  const leafNodes = operationItems
    .filter((item) => !hasDescendantNode(item, operationItems))
    .sort((a, b) => compareBranch(a.branch, b.branch))

  const matrix = leafNodes.map((leafNode) =>
    sortedRanks.map((rank) => {
      const matchingNode = operationItems
        .filter(
          (item) =>
            item.rank === rank && isBranchPrefix(item.branch, leafNode.branch)
        )
        .sort((a, b) => b.branch.length - a.branch.length)[0]

      return matchingNode
    })
  )

  return matrix
    .map((matrixRow, rowIndex) =>
      matrixRow.map((node, columnIndex) => {
        if (node === undefined) {
          return "no-op"
        }

        const previousNode = matrix[rowIndex - 1]?.[columnIndex]
        if (previousNode?.nodeId === node.nodeId) {
          return "no-op"
        }

        let rowspan = 1
        for (
          let nextRowIndex = rowIndex + 1;
          nextRowIndex < matrix.length &&
          matrix[nextRowIndex]?.[columnIndex]?.nodeId === node.nodeId;
          nextRowIndex += 1
        ) {
          rowspan += 1
        }

        return { op: node, rowspan }
      })
    )
    .filter((row) => row.some((cell) => cell !== "no-op"))
}

function isBranchPrefix(
  parentBranch: readonly number[],
  childBranch: readonly number[]
): boolean {
  if (parentBranch.length > childBranch.length) {
    return false
  }

  return parentBranch.every((value, index) => childBranch[index] === value)
}

function hasDescendantNode(
  operationItem: OperationItem,
  operationItems: readonly OperationItem[]
): boolean {
  return operationItems.some(
    (candidate) =>
      candidate.nodeId !== operationItem.nodeId &&
      candidate.rank > operationItem.rank &&
      isBranchPrefix(operationItem.branch, candidate.branch)
  )
}

function compareBranch(a: readonly number[], b: readonly number[]): number {
  const maxDepth = Math.min(a.length, b.length)
  for (let index = 0; index < maxDepth; index += 1) {
    const diff = a[index] - b[index]
    if (diff !== 0) {
      return diff
    }
  }

  return a.length - b.length
}
